import type { AfterNavigate, BeforeNavigate } from '@sveltejs/kit';
import { goto } from '$app/navigation';
import { stateNavigation } from '$states/navigation.svelte';
import { stateError } from '$states/error.svelte';
import { ErrorType } from '$types/error';
import { dev } from '$app/environment';

export abstract class Guard {
  /**
   * We count redirects to prevent infinite loops.
   */
  private static redirectCount = 0;
  private static lastRedirectTime = 0;
  private static readonly MAX_REDIRECTS = 30;
  private static readonly RESET_TIMEOUT = 1_000; /** 1 second */

  get target() {
    const pathname = this.event.to?.url.pathname;

    if (!pathname) {
      throw new Error('No pathname found');
    }

    return pathname;
  }

  constructor(private event: BeforeNavigate | AfterNavigate) {}

  /**
   * Redirects to the given path.
   *
   * Use this to guide a user to a different page.
   * @param to - target redirect destination
   */
  protected redirectTo = (to: string) => {
    const now = Date.now();

    if (now - Guard.lastRedirectTime > Guard.RESET_TIMEOUT) {
      Guard.redirectCount = 0;
    }

    Guard.redirectCount++;
    Guard.lastRedirectTime = now;

    const { warn } = console;

    if (dev) {
      warn(`Guard redirected from ${this.target} to ${to}`);
    }

    if (Guard.redirectCount > Guard.MAX_REDIRECTS) {
      stateError.set({
        type: ErrorType.SILENT,
        rawError: {
          stack: '',
          status: 500,
          errorId: 'Infinite redirect',
          message: `Infinite redirect loop detected (${Guard.redirectCount} redirects)`,
        },
        message: `Infinite redirect loop detected (${Guard.redirectCount} redirects)`,
      });
      Guard.redirectCount = 0;
      this._cancelEvent(this.event);
      return goto('/');
    }

    this._cancelEvent(this.event);
    return goto(to);
  };

  /**
   * Cancels navigation without a redirect
   * Redirects to the home page only if a user navigates via browser.
   *
   * Use this to simply prevent access without any redirect.
   */
  public cancel = () => {
    this._cancelEvent(this.event);

    const { warn } = console;

    if (dev) {
      warn(`Guard cancelled ${this.target}`);
    }

    if (!stateNavigation.last) {
      return goto('/');
    }
  };

  private _cancelEvent = (event: AfterNavigate | BeforeNavigate) => {
    if ('cancel' in event) event.cancel();
  };
}

export interface GuardRun {
  run: () => Promise<void> | void;
}
